import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api, {
  chessAPI,
  checkersAPI,
  draughtsAPI,
  flappyAPI,
  reactionAPI,
} from '../services/api';
import { getSocket } from '../services/socket';
import { connectWhotOnlineSocket, getWhotOnlineSocket } from '../services/whotOnlineSocket';

const PERMISSION_KEY = 'game_notifications_permission_requested_v1';
const DEDUPE_WINDOW_MS = 5000;
const FRIEND_POLL_MS = 15000;
const GAME_POLL_MS = 15000;
const TOAST_DURATION_MS = 6000;

function supportsBrowserNotifications() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

function normalizeGame(game) {
  return String(game || '').trim().toLowerCase();
}

function formatGameLabel(game) {
  const normalized = normalizeGame(game);
  switch (normalized) {
    case 'dice':
      return 'Dice';
    case 'whot':
      return 'WHOT';
    case 'flappy':
      return 'Flappy';
    case 'reaction':
      return 'Reaction';
    case 'chess':
      return 'Chess';
    case 'checkers':
      return 'Checkers';
    case 'draughts':
      return 'Draughts';
    case 'multiplayer':
      return 'Multiplayer Dice';
    default:
      return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Game';
  }
}

function isParticipant(match, userId) {
  const me = Number(userId || 0);
  return me > 0 && (
    Number(match?.creator_id || 0) === me ||
    Number(match?.opponent_id || 0) === me ||
    Number(match?.player1_id || 0) === me ||
    Number(match?.player2_id || 0) === me
  );
}

function getOpponentName(match, userId) {
  const me = Number(userId || 0);
  const creatorId = Number(match?.creator_id || match?.player1_id || 0);
  const opponentId = Number(match?.opponent_id || match?.player2_id || 0);

  if (creatorId === me) {
    return match?.opponent_username || match?.player2_username || 'your opponent';
  }
  if (opponentId === me) {
    return match?.creator_username || match?.player1_username || 'your opponent';
  }
  return (
    match?.opponent_username ||
    match?.creator_username ||
    match?.player2_username ||
    match?.player1_username ||
    'another player'
  );
}

function getChallengePath(challenge) {
  const game = normalizeGame(challenge?.game);
  if (game === 'whot') {
    return challenge?.id ? `/whot-online/room/${challenge.id}` : '/whot-online';
  }
  return challenge?.id ? `/challenges/${challenge.id}` : '/challenges';
}

function getLobbyPath(game) {
  switch (normalizeGame(game)) {
    case 'dice':
      return '/challenges';
    case 'whot':
      return '/whot-online';
    case 'flappy':
      return '/flappy';
    case 'reaction':
      return '/reaction';
    case 'chess':
      return '/chess';
    case 'checkers':
      return '/checkers/lobby';
    case 'draughts':
      return '/draughts/lobby';
    case 'multiplayer':
      return '/multiplayer';
    default:
      return '/dashboard';
  }
}

function getMatchPath(game, matchId) {
  if (!matchId) return getLobbyPath(game);

  switch (normalizeGame(game)) {
    case 'dice':
      return `/game/${matchId}`;
    case 'whot':
      return `/whot-online/room/${matchId}`;
    case 'flappy':
      return `/flappy/duel/${matchId}`;
    case 'reaction':
      return `/reaction/duel/${matchId}`;
    case 'chess':
      return `/chess/duel/${matchId}`;
    case 'checkers':
      return `/checkers/duel/${matchId}`;
    case 'draughts':
      return `/draughts/duel/${matchId}`;
    case 'multiplayer':
      return `/multiplayer/game/${matchId}`;
    default:
      return getLobbyPath(game);
  }
}

function getReactionModeLabel(gameMode) {
  const normalized = String(gameMode || '').trim().toLowerCase();
  switch (normalized) {
    case 'color_confusion':
      return 'Color Confusion';
    case 'tap_timing':
      return 'Tap Timing';
    case 'pattern_memory':
      return 'Pattern Memory';
    default:
      return normalized ? normalized.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Reaction';
  }
}

function getMatchCreatedMessage(game, match, userId) {
  const label = formatGameLabel(game);
  const opponent = getOpponentName(match, userId);

  switch (normalizeGame(game)) {
    case 'flappy':
      return {
        title: match?.opponent_id ? 'Flappy duel invite' : 'Flappy room opened',
        body: match?.opponent_id
          ? `${opponent} opened a flappy duel for you.`
          : `${match?.creator_username || 'A player'} opened a new flappy room.`,
      };
    case 'reaction':
      return {
        title: 'Reaction challenge created',
        body: `${opponent} opened a ${getReactionModeLabel(match?.game_mode)} duel.`,
      };
    case 'chess':
      return {
        title: match?.opponent_id ? '♟ Chess Rematch' : 'Chess challenge created',
        body: match?.opponent_id
          ? `${opponent} challenged you to a rematch. Tap to accept.`
          : `${opponent} opened a ${match?.mode === 'betting' ? 'betting' : 'friendly'} chess match.`,
      };
    case 'checkers':
      return {
        title: 'Checkers challenge created',
        body: `${opponent} opened a ${match?.mode === 'betting' ? 'betting' : 'friendly'} checkers match.`,
      };
    case 'draughts':
      return {
        title: 'Draughts challenge created',
        body: `${opponent} opened a ${match?.mode === 'betting' ? 'betting' : 'friendly'} draughts match.`,
      };
    default:
      return {
        title: `${label} challenge created`,
        body: `${opponent} opened a new ${label.toLowerCase()} match.`,
      };
  }
}

function getMatchStartedMessage(game, match, userId) {
  const label = formatGameLabel(game);
  const opponent = getOpponentName(match, userId);

  switch (normalizeGame(game)) {
    case 'dice':
      return {
        title: 'Dice quick match ready',
        body: `${opponent} is ready. Tap to roll.`,
      };
    case 'whot':
      return {
        title: 'WHOT room ready',
        body: `${opponent} is waiting in your WHOT room.`,
      };
    case 'flappy':
      return {
        title: 'Flappy duel started',
        body: `${opponent} is ready for your flappy run.`,
      };
    case 'reaction':
      return {
        title: 'Reaction duel started',
        body: `${getReactionModeLabel(match?.game_mode)} is live against ${opponent}.`,
      };
    case 'chess':
      return {
        title: 'Chess match started',
        body: `${opponent} is on the board now.`,
      };
    case 'checkers':
      return {
        title: 'Checkers match started',
        body: `${opponent} is waiting for your move.`,
      };
    case 'draughts':
      return {
        title: 'Draughts match started',
        body: `${opponent} is waiting for your move.`,
      };
    case 'multiplayer':
      return {
        title: 'Multiplayer dice started',
        body: 'Your multiplayer dice table is live.',
      };
    default:
      return {
        title: `${label} match started`,
        body: `${opponent} is ready.`,
      };
  }
}

function matchesPath(currentPath, targetPath) {
  if (!targetPath) return false;
  const current = String(currentPath || '').split('?')[0];
  const target = String(targetPath || '').split('?')[0];
  return current === target;
}

function ClickableToast({ title, body, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        maxWidth: 360,
        textAlign: 'left',
        background: '#111827',
        color: '#ffffff',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 14,
        padding: '12px 14px',
        boxShadow: '0 12px 30px rgba(0,0,0,0.28)',
        cursor: 'pointer',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{title}</div>
      {body ? <div style={{ fontSize: 13, opacity: 0.88, lineHeight: 1.35 }}>{body}</div> : null}
    </button>
  );
}

function showTimedClickableToast({ title, body, onClick }) {
  const toastId = toast.custom(
    (t) => (
      <ClickableToast
        title={title}
        body={body}
        onClick={() => {
          toast.dismiss(t.id);
          onClick?.();
        }}
      />
    ),
    {
      duration: TOAST_DURATION_MS,
      removeDelay: 300,
    }
  );

  window.setTimeout(() => {
    toast.dismiss(toastId);
  }, TOAST_DURATION_MS);
}

export default function GameNotifications() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const seenRef = useRef(new Map());

  useEffect(() => {
    if (!user?.id || !supportsBrowserNotifications()) return;
    if (Notification.permission !== 'default') return;
    if (localStorage.getItem(PERMISSION_KEY) === '1') return;

    localStorage.setItem(PERMISSION_KEY, '1');
    const timer = window.setTimeout(() => {
      Notification.requestPermission().catch(() => {});
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [user?.id]);

  useEffect(() => {
    if (!token) return;
    connectWhotOnlineSocket(token);
  }, [token]);

  useEffect(() => {
    if (!user?.id) return;

    const openPath = (path) => {
      if (!path || matchesPath(location.pathname + location.search, path)) return;
      navigate(path);
      try {
        window.focus();
      } catch {}
    };

    const shouldSkip = (dedupeKey) => {
      const now = Date.now();
      const prev = seenRef.current.get(dedupeKey) || 0;
      if ((now - prev) < DEDUPE_WINDOW_MS) return true;
      seenRef.current.set(dedupeKey, now);
      return false;
    };

    const notify = ({ title, body, path, dedupeKey }) => {
      if (!title || shouldSkip(dedupeKey)) return;

      if (document.visibilityState === 'visible') {
        showTimedClickableToast({
          title,
          body,
          onClick: () => openPath(path),
        });
        return;
      }

      if (supportsBrowserNotifications() && Notification.permission === 'granted') {
        try {
          const notification = new Notification(title, { body });
          notification.onclick = () => openPath(path);
          return;
        } catch {}
      }

      showTimedClickableToast({
        title,
        body,
        onClick: () => openPath(path),
      });
    };

    const socket = getSocket();
    const whotSocket = getWhotOnlineSocket();

    const onDirectChallenge = (challenge) => {
      if (!challenge?.id) return;
      notify({
        title: 'Dice challenge received',
        body: `${challenge.creator_username || 'A player'} invited you to a dice challenge.`,
        path: '/challenges',
        dedupeKey: `direct-${challenge.id}`,
      });
    };

    const onChallengeStarted = (challenge) => {
      if (!challenge?.id || !isParticipant(challenge, user.id)) return;
      const { title, body } = getMatchStartedMessage(challenge.game, challenge, user.id);
      notify({
        title,
        body,
        path: getChallengePath(challenge),
        dedupeKey: `challenge-started-${challenge.id}`,
      });
    };


    const onQuickGameStart = (payload) => {
      if (!payload?.gameId) return;
      const match = { creator_id: user.id, opponent_username: 'your opponent' };
      const { title, body } = getMatchStartedMessage('dice', match, user.id);
      notify({
        title,
        body: payload.message || body,
        path: getMatchPath('dice', payload.gameId),
        dedupeKey: `quick-${payload.gameId}`,
      });
    };

    const onOpponentJoined = (payload) => {
      if (!payload?.gameId) return;
      notify({
        title: 'Dice opponent joined',
        body: payload.message || 'Your dice game is ready to continue.',
        path: getMatchPath('dice', payload.gameId),
        dedupeKey: `opponent-${payload.gameId}`,
      });
    };

    const onWhotQueueMatched = (payload) => {
      if (!payload?.roomId) return;
      notify({
        title: 'WHOT queue matched',
        body: 'Your WHOT room is ready.',
        path: getMatchPath('whot', payload.roomId),
        dedupeKey: `whot-room-${payload.roomId}`,
      });
    };

    const onFriendRequestNew = (payload) => {
      notify({
        title: 'New friend request',
        body: `${payload?.fromUsername || 'Someone'} sent you a friend request.`,
        path: '/dashboard?friends=1',
        dedupeKey: `friend-request-live-${payload?.friendshipId || payload?.fromUserId || 'x'}`,
      });
    };

    const onFriendRequestAccepted = (payload) => {
      notify({
        title: 'Friend request accepted',
        body: `${payload?.acceptedByUsername || 'A player'} accepted your request.`,
        path: '/dashboard?friends=1',
        dedupeKey: `friend-accepted-live-${payload?.friendshipId || payload?.acceptedByUserId || 'x'}`,
      });
    };

    const onChatMessageNew = ({ message }) => {
      if (!message || Number(message.sender_id) === Number(user.id)) return;
      notify({
        title: `Message from ${message.sender_username || 'Friend'}`,
        body: String(message.content || '').slice(0, 120) || 'You have a new message.',
        path: '/dashboard?messages=1',
        dedupeKey: `chat-message-${message.id || `${message.sender_id}-${message.created_at || Date.now()}`}`,
      });
    };

    const onMultiplayerGameStarted = (payload) => {
      if (!payload?.challengeId) return;
      const { title, body } = getMatchStartedMessage('multiplayer', payload, user.id);
      notify({
        title,
        body: payload.message || body,
        path: getMatchPath('multiplayer', payload.challengeId),
        dedupeKey: `mp-start-${payload.challengeId}`,
      });
    };


    const onFlappyAccepted = (payload) => {
      const matchId = payload?.matchId || payload?.match?.id || payload?.id;
      if (!matchId) return;
      const { body } = getMatchStartedMessage('flappy', { id: matchId }, user.id);
      notify({
        title: 'Flappy duel accepted',
        body: body || 'Your flappy duel is ready.',
        path: getMatchPath('flappy', matchId),
        dedupeKey: `flappy-accepted-${matchId}`,
      });
    };

    const onFlappyStarted = (payload) => {
      const matchId = payload?.matchId;
      if (!matchId) return;
      const { title, body } = getMatchStartedMessage('flappy', { id: matchId }, user.id);
      notify({
        title,
        body,
        path: getMatchPath('flappy', matchId),
        dedupeKey: `flappy-started-${matchId}`,
      });
    };

    const onFlappyRechallengeCreated = (payload) => {
      if (!payload?.newMatchId) return;
      notify({
        title: 'Flappy rematch created',
        body: 'A new flappy rematch is ready to open.',
        path: getMatchPath('flappy', payload.newMatchId),
        dedupeKey: `flappy-rematch-${payload.newMatchId}`,
      });
    };

    const onReactionRematchCreated = (payload) => {
      if (!payload?.newMatchId) return;
      notify({
        title: 'Reaction rematch created',
        body: 'A new reaction rematch is ready.',
        path: getMatchPath('reaction', payload.newMatchId),
        dedupeKey: `reaction-rematch-${payload.newMatchId}`,
      });
    };

    if (socket) {
      socket.on('challenge:direct:new', onDirectChallenge);
      socket.on('challenge:started', onChallengeStarted);
      socket.on('game:start', onQuickGameStart);
      socket.on('opponent:joined', onOpponentJoined);
      socket.on('multiplayer:game-started', onMultiplayerGameStarted);
      socket.on('flappy:duel:accepted', onFlappyAccepted);
      socket.on('flappy:duel:started', onFlappyStarted);
      socket.on('flappy:duel:rechallenge-created', onFlappyRechallengeCreated);
      socket.on('reaction:duel:rematch-created', onReactionRematchCreated);
      socket.on('friend:request:new', onFriendRequestNew);
      socket.on('friend:request:accepted', onFriendRequestAccepted);
      socket.on('chat:message:new', onChatMessageNew);
    }

    if (whotSocket) {
      whotSocket.on('whot:queue:matched', onWhotQueueMatched);
    }

    return () => {
      if (socket) {
        socket.off('challenge:direct:new', onDirectChallenge);
        socket.off('challenge:started', onChallengeStarted);
        socket.off('game:start', onQuickGameStart);
        socket.off('opponent:joined', onOpponentJoined);
        socket.off('multiplayer:game-started', onMultiplayerGameStarted);
        socket.off('flappy:duel:accepted', onFlappyAccepted);
        socket.off('flappy:duel:started', onFlappyStarted);
        socket.off('flappy:duel:rechallenge-created', onFlappyRechallengeCreated);
        socket.off('reaction:duel:rematch-created', onReactionRematchCreated);
        socket.off('friend:request:new', onFriendRequestNew);
        socket.off('friend:request:accepted', onFriendRequestAccepted);
        socket.off('chat:message:new', onChatMessageNew);
      }

      if (whotSocket) {
        whotSocket.off('whot:queue:matched', onWhotQueueMatched);
      }
    };
  }, [location.pathname, location.search, navigate, user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;
    const knownReceivedRequestIds = new Set();
    const knownFriendIds = new Set();
    const onlineFriendMap = new Map();

    const notify = ({ title, body, path, dedupeKey }) => {
      const now = Date.now();
      const prev = seenRef.current.get(dedupeKey) || 0;
      if ((now - prev) < DEDUPE_WINDOW_MS) return;
      seenRef.current.set(dedupeKey, now);

      const onClick = () => {
        if (!matchesPath(location.pathname + location.search, path)) {
          navigate(path);
        }
        try {
          window.focus();
        } catch {}
      };

      if (document.visibilityState === 'visible') {
        showTimedClickableToast({
          title,
          body,
          onClick,
        });
        return;
      }

      if (supportsBrowserNotifications() && Notification.permission === 'granted') {
        try {
          const notification = new Notification(title, { body });
          notification.onclick = onClick;
          return;
        } catch {}
      }

      showTimedClickableToast({
        title,
        body,
        onClick,
      });
    };

    const primeState = async () => {
      try {
        const [pendingRes, friendsRes, onlineRes] = await Promise.all([
          api.get('/friends/pending'),
          api.get('/friends'),
          api.get('/friends/online-status'),
        ]);

        (pendingRes.data || [])
          .filter((entry) => entry.direction === 'received')
          .forEach((entry) => knownReceivedRequestIds.add(Number(entry.id)));

        (friendsRes.data || []).forEach((entry) => {
          if (entry?.friend_id) knownFriendIds.add(Number(entry.friend_id));
        });

        (onlineRes.data || []).forEach((entry) => {
          if (entry?.friend_id) {
            onlineFriendMap.set(Number(entry.friend_id), Boolean(entry.is_online));
          }
        });
      } catch (error) {
        console.error('Failed to prime notification friend state:', error);
      }
    };

    const pollFriendNotifications = async () => {
      try {
        const [pendingRes, friendsRes, onlineRes] = await Promise.all([
          api.get('/friends/pending'),
          api.get('/friends'),
          api.get('/friends/online-status'),
        ]);

        if (cancelled) return;

        (pendingRes.data || [])
          .filter((entry) => entry.direction === 'received')
          .forEach((entry) => {
            const requestId = Number(entry.id);
            if (!requestId || knownReceivedRequestIds.has(requestId)) return;
            knownReceivedRequestIds.add(requestId);
            notify({
              title: 'New friend request',
              body: `${entry.requester_username || 'Someone'} sent you a friend request.`,
              path: '/dashboard?friends=1',
              dedupeKey: `friend-request-${requestId}`,
            });
          });

        (friendsRes.data || []).forEach((entry) => {
          const friendId = Number(entry.friend_id || 0);
          if (!friendId || knownFriendIds.has(friendId)) return;
          knownFriendIds.add(friendId);
          notify({
            title: 'Friend request accepted',
            body: `${entry.friend_username || 'A player'} is now your friend.`,
            path: '/dashboard?friends=1',
            dedupeKey: `friend-accepted-${friendId}`,
          });
        });

        (onlineRes.data || []).forEach((entry) => {
          const friendId = Number(entry.friend_id || 0);
          const isOnline = Boolean(entry.is_online);
          if (!friendId) return;

          const previous = onlineFriendMap.get(friendId);
          onlineFriendMap.set(friendId, isOnline);

          if (previous === false && isOnline === true) {
            notify({
              title: 'Friend online',
              body: `${entry.friend_username || 'Your friend'} is online now.`,
              path: '/dashboard?friends=1',
              dedupeKey: `friend-online-${friendId}`,
            });
          }
        });
      } catch (error) {
        console.error('Failed to poll friend notifications:', error);
      }
    };

    let intervalId = null;
    primeState().then(() => {
      if (cancelled) return;
      void pollFriendNotifications();
      intervalId = window.setInterval(() => {
        void pollFriendNotifications();
      }, FRIEND_POLL_MS);
    });

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [location.pathname, location.search, navigate, user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;
    const knownPendingKeys = new Set();
    const knownOngoingKeys = new Set();

    const notify = ({ title, body, path, dedupeKey }) => {
      const now = Date.now();
      const prev = seenRef.current.get(dedupeKey) || 0;
      if ((now - prev) < DEDUPE_WINDOW_MS) return;
      seenRef.current.set(dedupeKey, now);

      const onClick = () => {
        if (!matchesPath(location.pathname + location.search, path)) {
          navigate(path);
        }
        try {
          window.focus();
        } catch {}
      };

      if (document.visibilityState === 'visible') {
        showTimedClickableToast({
          title,
          body,
          onClick,
        });
        return;
      }

      if (supportsBrowserNotifications() && Notification.permission === 'granted') {
        try {
          const notification = new Notification(title, { body });
          notification.onclick = onClick;
          return;
        } catch {}
      }

      showTimedClickableToast({
        title,
        body,
        onClick,
      });
    };

    const configs = [
      {
        game: 'flappy',
        listChallenges: () => flappyAPI.listChallenges(),
        listOngoing: () => flappyAPI.listOngoingMatches(),
      },
      {
        game: 'reaction',
        listChallenges: () => reactionAPI.listChallenges(),
        listOngoing: () => reactionAPI.listOngoingMatches(),
      },
      {
        game: 'chess',
        listChallenges: () => chessAPI.listChallenges(),
        listOngoing: () => chessAPI.listOngoingMatches(),
      },
      {
        game: 'checkers',
        listChallenges: () => checkersAPI.listChallenges(),
        listOngoing: () => checkersAPI.listOngoingMatches(),
      },
      {
        game: 'draughts',
        listChallenges: () => draughtsAPI.listChallenges(),
        listOngoing: () => draughtsAPI.listOngoingMatches(),
      },
    ];

    const primeGameState = async () => {
      try {
        const results = await Promise.all(configs.flatMap((config) => [
          config.listChallenges().then((res) => ({ type: 'challenge', game: config.game, items: res.data || [] })),
          config.listOngoing().then((res) => ({ type: 'ongoing', game: config.game, items: res.data || [] })),
        ]));

        results.forEach(({ type, game, items }) => {
          (items || []).forEach((item) => {
            if (!item?.id || !isParticipant(item, user.id)) return;
            const key = `${game}-${item.id}`;
            if (type === 'challenge') knownPendingKeys.add(key);
            if (type === 'ongoing') knownOngoingKeys.add(key);
          });
        });
      } catch (error) {
        console.error('Failed to prime game notifications:', error);
      }
    };

    const pollGameNotifications = async () => {
      try {
        const results = await Promise.all(configs.flatMap((config) => [
          config.listChallenges().then((res) => ({ type: 'challenge', game: config.game, items: res.data || [] })),
          config.listOngoing().then((res) => ({ type: 'ongoing', game: config.game, items: res.data || [] })),
        ]));

        if (cancelled) return;

        results.forEach(({ type, game, items }) => {
          (items || []).forEach((item) => {
            if (!item?.id || !isParticipant(item, user.id)) return;

            const key = `${game}-${item.id}`;

            if (type === 'challenge') {
              if (knownPendingKeys.has(key)) return;
              knownPendingKeys.add(key);

              const { title, body } = getMatchCreatedMessage(game, item, user.id);
              // For targeted challenges (rematch / direct invite), send opponent straight to the match.
              // For open challenges (no opponent_id), send to the lobby.
              const isTargeted = item.opponent_id && Number(item.opponent_id) === Number(user.id);
              notify({
                title,
                body,
                path: isTargeted ? getMatchPath(game, item.id) : getLobbyPath(game),
                dedupeKey: `poll-created-${key}`,
              });
              return;
            }

            if (knownOngoingKeys.has(key)) return;
            knownOngoingKeys.add(key);

            const { title, body } = getMatchStartedMessage(game, item, user.id);
            notify({
              title,
              body,
              path: getMatchPath(game, item.id),
              dedupeKey: `poll-started-${key}`,
            });
          });
        });
      } catch (error) {
        console.error('Failed to poll game notifications:', error);
      }
    };

    let intervalId = null;
    primeGameState().then(() => {
      if (cancelled) return;
      void pollGameNotifications();
      intervalId = window.setInterval(() => {
        void pollGameNotifications();
      }, GAME_POLL_MS);
    });

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [location.pathname, location.search, navigate, user?.id]);

  return null;
}
